import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { RemoveTypenameFromVariablesLink } from "@apollo/client/link/remove-typename";

import { ApolloProvider } from "@apollo/client/react";
import { ApplicationInsightsLink } from "../lib/apollo-insights-link";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { ErrorLink } from "@apollo/client/link/error";
import { HttpLink } from "@apollo/client/link/http";
import { LoadingComponent } from "../components/loading-component";
import { ReactNode } from "react";
import { SetContextLink } from "@apollo/client/link/context";
import { routes } from "../routes/constants";
import { showNotification } from "../components/notification";
import { useAuth0 } from "@auth0/auth0-react";

interface GraphQlProviderProps {
  children: ReactNode;
}

// Interface for Auth0 error object
interface Auth0Error {
  error?: string;
  message?: string;
}

// Type guard to check if error has Auth0 error properties
const isAuth0Error = (error: unknown): error is Auth0Error => {
  return typeof error === "object" && error !== null;
};

export const GraphqlProvider = ({ children }: GraphQlProviderProps) => {
  const { getAccessTokenSilently, getAccessTokenWithPopup, user, error: errorAuth0, isLoading } = useAuth0();

  const authMiddleware = new SetContextLink(async (prevContext) => {
    if (errorAuth0) {
      window.location.replace(routes.error.path);
      return {};
    }
    let token;
    if (user) {
      const fetchAccessToken = async () => {
        try {
          token = await getAccessTokenSilently();
        } catch (e) {
          if (isAuth0Error(e) && e.error === "consent_required") {
            token = await getAccessTokenWithPopup();
          } else {
            const errorMessage = isAuth0Error(e) && e.message ? e.message : "Authentication error";
            showNotification({ variant: "error", message: errorMessage });
            window.location.replace(routes.error.path);
          }
        }
      };
      await fetchAccessToken();
    }

    return {
      headers: {
        ...prevContext.headers,
        authorization: `Bearer ${token}`,
      },
    };
  });

  const errorLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      const errorMessage = error.errors[0]?.message;
      if (errorMessage) {
        showNotification({ variant: "error", message: `[GraphQL error]: ${errorMessage}` });
      }
    } else {
      // Network or other errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      showNotification({
        variant: "error",
        message: `Network error: ${errorMessage}`,
      });
    }
  });

  const removeTypenameLink = new RemoveTypenameFromVariablesLink();

  const httpLink = new HttpLink({ uri: import.meta.env.VITE_API_SITES_BASE_URL });

  const graphQlClient = new ApolloClient({
    link: ApolloLink.from([new ApplicationInsightsLink(), authMiddleware, errorLink, removeTypenameLink, httpLink]),
    cache: new InMemoryCache(),
  });

  if (isLoading) {
    return <LoadingComponent />;
  }
  return <ApolloProvider client={graphQlClient}>{children}</ApolloProvider>;
};
